import { Boss, type BossDef } from './boss-base';
import { getTexture } from '@/assets/resolver';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { Cocoon } from './webspinner-cocoon';

/**
 * Webspinner — strings LASER THREADS across the arena, slicing
 * it into shrinking safe lanes (area denial). Each web is telegraphed first
 * (you always get time to pick a side), then the thread lingers. Between webs
 * it fires ordinary fans. It hides in a SILK COCOON (wave 4): the panel
 * facing a shot damps it to a fraction until it is shot apart; holes gape,
 * then knit back (webspinner-cocoon.ts, BOSS_MECHANICS.webspinner).
 * docs/02 boss pool.
 * Signatures: silk cocoon + telegraphed laser webs (always), aimed fan (P1/P2).
 */
export const WEBSPINNER: BossDef = {
  id: 'webspinner',
  nameKey: 'boss.webspinner',
  tier: 4,
  minRoom: 3,
  baseHp: 1400, // the cocoon damps hits — a lighter hull keeps TTK
  radius: 50,
  bodyKey: 'boss/webspinner/body',
  movement: 'hover',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          // Silk beads: they brake to a halt and hang like dew (B3 brake)
          type: 'aimed', bullet: 'bullet/enemy/ball', count: 3, spreadDeg: 22,
          interval: 2.6, telegraph: 0.4, speed: 230, radius: 10, damage: 1, life: 4.5,
          motion: { accel: -110, minSpeed: 0 },
        },
      ],
    },
    {
      hpPct: 0.5,
      speedMul: 1.15,
      patterns: [
        {
          type: 'aimed', bullet: 'bullet/enemy/ball', count: 5, spreadDeg: 34,
          interval: 2.4, telegraph: 0.35, speed: 245, radius: 10, damage: 1,
        },
        {
          type: 'radial', bullet: 'bullet/enemy/ring', count: 10, interval: 3.6,
          spinDeg: 16, speed: 110, radius: 10, damage: 2, unlockTier: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Silk spray: a loose ring of beads between webs
      tier: 2,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/ball', count: 10, interval: 4.0,
        spinDeg: 18, speed: 110, radius: 10, damage: 1,
      },
    },
    {
      // Spinneret: a lazy twin-arm spiral once the abdomen cracks
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'spiral', bullet: 'bullet/enemy/ball', arms: 2, interval: 0.24,
        angularSpeed: 55, speed: 120, radius: 9, damage: 1,
      },
    },
  ],
};

const WEB_INTERVAL = 7.5;
const WEB_WINDUP = 0.9; // telegraph before a thread snaps taut
const THREAD_LIFE = 3.2;
const THREAD_STEP = 34; // spacing of bead bullets along a thread
const THREAD_SPAN = 0.5; // half-length of a thread (× arena diagonal)

export class WebspinnerBoss extends Boss {
  private webTimer = WEB_INTERVAL * 0.5;
  private webWind = 0;
  private angle = 0.4;
  private readonly cocoon = new Cocoon((a) => {
    const r = this.def.radius;
    this.events?.burst(this.x + Math.cos(a) * r, this.y + Math.sin(a) * r, BOSS_FEEDBACK.webspinnerTear);
  });

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(WEBSPINNER, level, x, y, homeX, homeY);
    this.allowReinforce = false;
    this.view.addChild(this.cocoon.view); // silk wraps OVER the body
    this.cocoon.weave(this.maxHp);
  }

  /** The silk scales with the hull (firepower / sector HP multipliers). */
  protected override scaleParts(_mul: number): void {
    this.cocoon.weave(this.maxHp);
  }

  /** Every hit goes through the panel facing it — numbers show the damped share. */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    const before = this.hp;
    const hits = bullets.collideCircle(true, this.x, this.y, this.def.radius, (dmg, bx, by) =>
      this.landHit(this.cocoon.filter(Math.atan2(by - this.y, bx - this.x), dmg)));
    return { hits, dealt: before - Math.max(0, this.hp) };
  }

  /** Blasts tear the silk on their side (bombs shred it fast). */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (this.dead || !this.hitByAoe(x, y, radius)) return 0;
    this.markAoe(this.x, this.y, this.def.radius);
    return this.landHit(this.cocoon.filter(Math.atan2(y - this.y, x - this.x), damage));
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;
    this.cocoon.update(dt);

    if (this.webWind > 0) {
      this.webWind -= dt;
      if (this.webWind <= 0) this.spinWeb(bullets);
    } else {
      this.webTimer -= dt;
      if (this.webTimer <= 0) {
        this.webTimer = WEB_INTERVAL / this.intensity;
        // Two crossing threads — extra one past half HP
        this.angle += 1.1;
        this.webWind = WEB_WINDUP;
        if (telegraphs) {
          // Silk pulls taut along the FULL thread before it snaps live
          const span = Math.hypot(this.arenaW, this.arenaH) * THREAD_SPAN;
          const count = this.hpPct <= 0.5 ? 3 : 2;
          for (let k = 0; k < count; k++) {
            const a = this.angle + (k * Math.PI) / count;
            telegraphs.strand(this.homeX, this.homeY, Math.cos(a), Math.sin(a), span, WEB_WINDUP);
          }
        }
      }
    }
  }

  private spinWeb(bullets: BulletSystem): void {
    // Threads span the REAL arena (X3) — layouts differ from GAME.arena
    const span = Math.hypot(this.arenaW, this.arenaH) * THREAD_SPAN;
    const count = this.hpPct <= 0.5 ? 3 : 2;
    for (let k = 0; k < count; k++) {
      const a = this.angle + (k * Math.PI) / count;
      const ux = Math.cos(a);
      const uy = Math.sin(a);
      for (let s = -span; s <= span; s += THREAD_STEP) {
        bullets.spawn({
          x: this.homeX + ux * s, y: this.homeY + uy * s, dirX: 0, dirY: 0,
          speed: 0, life: THREAD_LIFE, radius: 9, damage: 2,
          friendly: false, texKey: 'bullet/enemy/ring', rotates: false,
        });
      }
    }
  }

  override render(alpha: number): void {
    super.render(alpha);
    this.body.texture = getTexture('boss/webspinner/body', this.webWind > 0 ? 1 : 0);
    this.cocoon.render();
  }
}
